import { IconButton, Menu, MenuItem, TableHead } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TCell, { TableCellProps } from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableFooter from '@material-ui/core/TableFooter';
import TablePagination from '@material-ui/core/TablePagination';
import TRow, { TableRowProps } from '@material-ui/core/TableRow';
import MoreIcon from '@material-ui/icons/MoreVert';
import i18n from 'i18n';
import OrderIcon from '@material-ui/icons/ImportExport';
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state';
import React from 'react';
import { cyrb53Hash, isANumberValue, parseNameLdap } from 'utils/helpers';
import clsx from 'clsx';
import styles from './styles';

const useStyles = makeStyles(styles);

export interface LabelsProps {
  align?: boolean;
  key: string;
  title: string;
}

export interface TableProps {
  count: number;
  fields: string[][];
  items: Array<any>;
  labels: LabelsProps[];
  noAction?: boolean;
  rowsPerPage: number;
  getItemToOrder?: (value: string) => void;
  handleEdit?: (item: any) => void;
  handleRemove?: (item: any) => void;
  onChangePage: (page: number) => void;
  onChangeLimit: (limit: number) => void;
}

function TableRow(props: TableRowProps) {
  const classes = useStyles();
  return <TRow className={classes.row} {...props} />;
}

function TableCell(props: TableCellProps) {
  const classes = useStyles();
  return <TCell className={classes.cell} {...props} />;
}

function MenuActions({ item, handleEdit, handleRemove }) {
  const classes = useStyles();
  return (
    <PopupState variant="popover">
      {popupState => (
        <>
          <IconButton
            className={classes.moreButton}
            {...bindTrigger(popupState)}
          >
            <MoreIcon className={classes.moreButtonIcon} />
          </IconButton>
          <Menu {...bindMenu(popupState)}>
            {handleEdit && (
              <MenuItem
                id="btn-edit-company-related"
                className={classes.actionItem}
                onClick={() => {
                  popupState.close();
                  handleEdit(item);
                }}
              >
                {i18n.t('EDIT_KEY')}
              </MenuItem>
            )}
            {handleRemove && (
              <MenuItem
                id="btn-remove-company-related"
                className={classes.actionItem}
                onClick={() => {
                  popupState.close();
                  handleRemove(item);
                }}
              >
                {i18n.t('REMOVE_KEY')}
              </MenuItem>
            )}
          </Menu>
        </>
      )}
    </PopupState>
  );
}

export default function TableTab({
  count,
  fields,
  items,
  labels,
  noAction = false,
  rowsPerPage: rows,
  getItemToOrder,
  handleEdit,
  handleRemove,
  onChangePage,
  onChangeLimit,
}: TableProps) {
  const classes = useStyles();

  const [page, setPage] = React.useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(rows || 5);

  const handleGetOrder = (value: string) => {
    if (getItemToOrder) {
      getItemToOrder(value);
    }
  };

  const getValue = (row: string[], cell: string) =>
    row[2] ? parseNameLdap(cell) : cell;

  const valueIsNumber = (row: string[], cell: string): boolean =>
    isANumberValue(getValue(row, cell));

  return (
    <TableContainer component={Paper} data-testid={'autosky-tabletab'}>
      <Table>
        <TableHead>
          <TableRow className={classes.withoutHover}>
            {labels.map(label => (
              <TableCell
                onClick={() => handleGetOrder(label.key)}
                key={label.key}
                className={classes.cell}
                align={label?.align ? 'center' : 'left'}
              >
                <div
                  className={`${classes.titleContainer} ${label?.align &&
                    classes.center}`}
                >
                  {label.title}
                  <OrderIcon className={classes.orderIcon} />
                </div>
              </TableCell>
            ))}
            {!noAction && <TableCell></TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {fields.map((row, i) => (
            <TableRow key={cyrb53Hash(row?.[i])}>
              {row.map((cell, cellIndex) => (
                <TableCell
                  key={cell}
                  className={clsx(classes.cell, {
                    [classes.numberCell]:
                      labels[cellIndex]?.align || valueIsNumber(row, cell),
                  })}
                >
                  {getValue(row, cell)}
                </TableCell>
              ))}
              {!noAction && (
                <TableCell style={{ width: 10 }}>
                  <MenuActions
                    item={items[i]}
                    handleEdit={handleEdit}
                    handleRemove={handleRemove}
                  />
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>

        <TableFooter>
          <TableRow className={classes.withoutHover}>
            <TablePagination
              labelRowsPerPage={i18n.t('ROWS_PER_PAGE_KEY')}
              rowsPerPageOptions={[5, 10, 25, 100]}
              colSpan={8}
              count={count}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{ native: true }}
              onChangePage={(e, newPage) => {
                setPage(newPage);
                onChangePage(newPage);
              }}
              onChangeRowsPerPage={({ target }) => {
                setRowsPerPage(+target.value);
                onChangeLimit(+target.value);
                setPage(0);
                onChangePage(0);
              }}
              className={classes.pagination}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}
